'use client';

import { useEffect, useRef, useState } from 'react';
import type { Chat } from '@repo/api';
import { ChatListItem } from './ChatListItem';
import React from 'react';
import { Divider } from '../ui';
import { cn } from '@/lib/cn';

interface ChatListProps {
  chats: Chat[];
  selectedChatId?: string | null;
  onChatSelect: (chat: Chat) => void;
}

export function ChatList({ chats, selectedChatId, onChatSelect }: ChatListProps) {
  const selectedRef = useRef<HTMLDivElement>(null);
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);

  // Scroll selected chat into view
  useEffect(() => {
    if (selectedChatId && selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedChatId]);

  return (
    <div className="flex flex-col">
      {chats.map((chat, index) => {
        const isSelected = chat.id === selectedChatId;
        const isHovered = chat.id === hoveredChatId;
        const nextChat = index < chats.length - 1 ? chats[index + 1] : undefined;
        const isNextSelected = nextChat?.id === selectedChatId;
        const isNextHovered = nextChat?.id === hoveredChatId;

        const shouldHideDivider = isSelected || isHovered || isNextSelected || isNextHovered;

        return (
          <React.Fragment key={chat.id}>
            <div
              key={chat.id}
              ref={isSelected ? selectedRef : null}
              onMouseEnter={() => setHoveredChatId(chat.id)}
              onMouseLeave={() => setHoveredChatId(null)}
            >
              <ChatListItem
                chat={chat}
                isSelected={isSelected}
                onClick={() => onChatSelect(chat)}
              />
            </div>
            {index < chats.length - 1 && (
              <Divider
                className={cn(
                  'my-1 transition-opacity',
                  shouldHideDivider ? 'opacity-0' : 'opacity-100',
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
